import orgamax from 'ampersand-app'
import moment from 'moment/moment'
import q from 'q'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import React from 'react'

import lang from '@/lang'
import accountConfig from '@/modules/account/config'
import settingsConfig from '@/modules/settings/config'
import openOnboardingModal from '@/modules/_helpers/openOnboardingModal'
import { isAndroid, isIos } from '@/modules/_helpers/detectMobile'
import { ProductTypes, subscriptionLimits } from '@/modules/base/util/constants'
import { UNSAFE_openUpgradeModal as openUpgradeModal } from '@/hooks/useUpgradeModal'
import { UNSAFE_openModal as openModal } from '@/modules/_helpers/openModal'
import PausedModal from '@/modules/settings/components/account/paused-modal/paused-modal'
import store from '@/redux/store'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { setModulesAccessBySubscription } from '@/redux/ducks/permission/moduleAccess'
import openDanglingSubscriptionModal from '@/modules/_helpers/openDanglingSubscriptionModal'
import openClientInvitationsModal from '@/modules/_helpers/openClientInvitationsModal'
import { fetchData } from '@/redux/ducks/user/info'
import openTrialSelectionModal from '@/modules/subscription/_helpers/openTrialSelectionModal'
import startInitialTrial from '@/modules/subscription/_helpers/startInitialTrial'
import { isEqual, orderBy } from 'lodash'
import openNoRoleAssignedModal from '@/modules/_helpers/openNoRoleAssignedModal'
import openConfirmEmailModal from '@/modules/_helpers/openConfirmEmailModal'
import openAppAdviceModal from '@/modules/dashboard/helpers/openAppAdviceModal'
import { DetailsType } from './types/details'
import { getOnboardingGate, needsDoiPrompt } from './getOnboardingGate'
import request from '@/modules/base/util/request'
import { awaitableModal } from '@/modules/base/components/modal/_helpers/awaitableModal'
import AcceptAvvModal from '@/modules/account/components/AcceptAvvModal'
import CelloService from '@/modules/cello/services/cello.service'
import { isMeinBuero } from '@/utils/build-env'

// actions
const SET_ACTIVE_SUBSCRIPTION = 'subscription/SET_ACTIVE_SUBSCRIPTION'
const SET_SELECTED_SUBSCRIPTION = 'subscription/SET_SELECTED_SUBSCRIPTION'
const SET_SUBSCRIPTION_ERROR = 'subscription/SET_SUBSCRIPTION_ERROR'

// reducer
const initialState: DetailsType = {
  lastFetch: null,
  loading: true,
  subscriptions: [],
  selectedSubscriptionId: null,
  activeDetails: [],
  hasBackupAddon: false,
  backupDashboardMode: 'hidden',
  hasPublicApiAccess: false,
}

const isMB = isMeinBuero()

export default function reducer(state = initialState, action): DetailsType {
  switch (action.type) {
    case SET_ACTIVE_SUBSCRIPTION:
      const { data } = action
      // ensure we have a clean state
      return {
        ...initialState,
        ...data,
        hasDanglingSubscriptions: data.hasDanglingSubscriptions || false,
        lastFetch: moment(),
        loading: false,
      }
    case SET_SELECTED_SUBSCRIPTION:
      const { selectedSubscriptionId } = action
      return { ...state, selectedSubscriptionId }

    case SET_SUBSCRIPTION_ERROR:
      // terminate the loading state so consumers render a degraded view instead of an endless loader.
      // lastFetch stays untouched on purpose, so the next call retries (see fetchSubscriptionData)
      return { ...state, loading: false, error: action.error ?? true }

    default:
      return state
  }
}

// action creators
const setActiveSubscription = (data) => {
  return {
    type: SET_ACTIVE_SUBSCRIPTION,
    data,
  }
}

export const setSelectedSubscription = (selectedSubscriptionId) => {
  return {
    type: SET_SELECTED_SUBSCRIPTION,
    selectedSubscriptionId,
  }
}

export const setSubscriptionError = (error) => {
  return {
    type: SET_SUBSCRIPTION_ERROR,
    error,
  }
}

const onFetchError = (error) => {
  console.error(error)
  toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
}

export const fetchSubscriptionData = async(force = false, cb = null) => {
  const { lastFetch, confirmation = {}, selectedSubscriptionId, ...subscriptionDetail } = store.getState().subscription?.details || {}
  let subscription = { ...subscriptionDetail, confirmation, lastFetch }
  if (!lastFetch || moment(lastFetch).add(1, 'minutes') < moment() || force || needsDoiPrompt(confirmation)) {
    const requests = [
      request(settingsConfig.getSubscriptionDetails, { auth: true, isConcurrent: true }),
      request(accountConfig.endpoints.getRegistrationConfirmation, {
        auth: true,
        method: 'GET',
        isConcurrent: true,
      }),
    ]

    let subscriptionDetailResponse
    let registrationConfirmationResponse
    try {
      [subscriptionDetailResponse, registrationConfirmationResponse] = await q.all(requests)
    } catch (error) {
      // without this the loading flag would stay true forever and every consumer
      // (IndexRedirect, ProtectedRoute, lists) would render an endless loader
      store.dispatch(setSubscriptionError(error))
      throw error
    }
    const {
      body: {
        data,
      },
    } = subscriptionDetailResponse
    const { body: { data: confirmationData } } = registrationConfirmationResponse

    if (subscription?.subscriptions?.length || (lastFetch && data?.subscriptions?.length)) {
      if (!subscription?.subscriptions?.length && data?.subscriptions?.length) {
        orgamax.user.initPendo()
      } else {
        const subsBefore = subscription.subscriptions.map(s => ({ type: s.type, end: s.end }))
        const subsAfter = data.subscriptions.map(s => ({ type: s.type, end: s.end }))
        if (subsBefore.length !== subsAfter.length || !isEqual(orderBy(subsBefore, 'type'), orderBy(subsAfter, 'type'))) {
          orgamax.user.initPendo()
        }
      }
    }

    const allSubscriptionsInactive = data?.subscriptions?.every(s => s.isExpired || s.isSubscriptionLocked)
    subscription = { ...data, confirmation: confirmationData, allSubscriptionsInactive }

    setModulesAccessBySubscription(subscription)
    // set selected subscription id if not set or if selected subscription is not available anymore (tenant switch)
    if (subscription?.subscriptions?.length && (!selectedSubscriptionId || !subscription.subscriptions.find(s => s.id === selectedSubscriptionId))) {
      store.dispatch(setSelectedSubscription(subscription.subscriptions[0]?.id || null))
    }
    store.dispatch(setActiveSubscription(subscription))
    const tenantRestores = store.getState().user?.info?.activeTenant?.tenantRestores || []
    const restoreTokenAvailable = !!/restore\/?.*/.test(window.location.pathname) || !!tenantRestores?.length
    if (!restoreTokenAvailable) {
      if (subscription.hasDanglingSubscriptions && !orgamax.user.isDesktopTenant && !WebStorageService.getItem(WebStorageKey.DANGLING_SUBSCRIPTION_LATER)) {
        WebStorageService.setItem(WebStorageKey.DANGLING_SUBSCRIPTION_LATER, true)
        openDanglingSubscriptionModal()
      } else {
        if (!WebStorageService.getItem(WebStorageKey.NO_ROLE_ASSIGNED_CONFIRMED)) {
          const userInfo = store.getState().user.info
          const tenantInfo = store.getState().tenant.info
          if ((userInfo.roles?.includes('NO_TENANT_ROLE') && !userInfo.isOwner)
          || (userInfo.isOwner && tenantInfo.tenantUsersWithoutRoleCount > 0)) {
            openNoRoleAssignedModal({ isOwner: userInfo?.isOwner })
          }
        }
      }
    }
  }

  cb?.(subscription)
  return subscription
}

export const updateSubscriptionDetails = (force = false, withUserInfo = false) => {
  const setSubscriptionState = (subscriptionDetail) => {
    const {
      subscriptions,
      confirmation,
      activeDetails,
      begin,
    } = subscriptionDetail

    const subscriptionData = subscriptions.reduce((result, subscription) => {
      result.subscriptions.push({
        begin: subscription.begin,
        end: subscription.end,
        type: subscription.type,
        isTrial: subscription.isTrial,
      })

      return result
    }, { activeDetails, subscriptions: [], begin })

    const { hasConfirmedEmail, email, avv, onboardingFinished } = confirmation || {}
    let isAvvAccepted = true

    orgamax.user.subscriptionData = subscriptionData
    orgamax.user.registrationConfirmation = { hasConfirmedEmail, email, avv }

    orgamax.trigger('userModelSubscriptionDataSet')

    const { allSubscriptionsLocked } = orgamax.user

    if (avv) {
      isAvvAccepted = avv.isAccepted
    }
    const today = moment()

    const hasNotTrialSub = subscriptions?.some(s => s.isTrial === false)
    const isTrial = !hasNotTrialSub
    const allSubscriptionsExpired = !subscriptions?.find(s => !s.end || moment(s.end).isAfter(today))
    const allSubscriptionsCancelled = !subscriptions?.find(s => s.isCancelled === false)
    const isTaxConsultant = subscriptions?.some(s => s.typeOfSubscription === ProductTypes.TAX_CONSULTANT)

    const linkShopError = WebStorageService.getItem(WebStorageKey.SHOP_ERROR)
    const readOnlyConfirmed = WebStorageService.getItem(WebStorageKey.READ_ONLY_CONFIRMED)
    const isLockedConfirmed = WebStorageService.getItem(WebStorageKey.IS_LOCKED_CONFIRMED)
    const overrideMobileWarning = WebStorageService.getItem(WebStorageKey.OVERRIDE_MOBILE_WARNING)

    // hide the chat for MB if the user has a non-trial subscription
    if (!isTrial && isMB) {
      const hubspotChat = document.getElementById('hubspot-messages-iframe-container')
      if (hubspotChat) {
        hubspotChat.style.setProperty('display', 'none', 'important')
      }
    }

    const { step } = store?.getState()?.wizard.config

    if (step > 0 && !onboardingFinished) {
      return subscriptionDetail
    }
    const gate = getOnboardingGate(confirmation, {
      hasSubscriptions: !!subscriptions?.length,
      isMobileDevice: isAndroid() || isIos(),
      isTaxConsultant,
      overrideMobileWarning: !!overrideMobileWarning,
      isAvvAccepted,
      allSubscriptionsExpired,
      allSubscriptionsCancelled,
      readOnlyConfirmed: !!readOnlyConfirmed,
      hasLinkShopError: !!linkShopError,
      isTrial,
      allSubscriptionsLocked,
      isLockedConfirmed: !!isLockedConfirmed,
    })

    switch (gate) {
      case 'confirmEmail':
        openConfirmEmailModal({ showLogout: true, isRegistration: true })
        break
      case 'onboarding':
        openOnboardingModal()
        break
      case 'trial':
        if (isMB) {
          openTrialSelectionModal()
        } else {
          startInitialTrial()
        }
        break
      case 'appAdvice':
        openAppAdviceModal()
        break
      case 'avv':
        awaitableModal({
          isOpen: true,
          showCloseIcon: false,
          cancelReject: true,
          component: AcceptAvvModal,
          width: 560,
          email,
        })
        break
      case 'expired': {
        const filteredSubs = subscriptions.filter(sub =>
          [
            ProductTypes.MAIN,
            ProductTypes.DOCUMENT,
            ProductTypes.STAFF,
          ].includes(sub.typeOfSubscription),
        )
        openUpgradeModal({
          trialExpired: true,
          subscriptionType: filteredSubs.length === 1
            ? filteredSubs[0].typeOfSubscription
            : 'all',
        })
        break
      }
      case 'shopError':
        if (linkShopError?.meta?.[subscriptionLimits.SHOP_COUNT]) {
          openUpgradeModal({
            limitReached: subscriptionLimits.SHOP_COUNT,
            subscriptionChange: true,
          })
        } else {
          toast.showToast({ type: 'error', message: lang.eCommerce.linkError })
        }

        WebStorageService.removeItem(WebStorageKey.SHOP_ERROR)
        break
      case 'paused':
        openModal(close => ({
          component: 'ModalBig',
          props: {
            disableEscapeKeyDown: true,
            disableBackdropClick: true,
            showCloseIcon: false,
          },
          children: [<PausedModal key={'update'} buttonCallback={() => {
            WebStorageService.setItem(WebStorageKey.IS_LOCKED_CONFIRMED, true)
            close()
          }} />],
        }))
        break
    }

    if (onboardingFinished && !isTaxConsultant && !WebStorageService.getItem(WebStorageKey.OPEN_DASHBOARD_CLIENT_INVITATIONS)) {
      request(settingsConfig.getInvitingTenants, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      })
        .then(response => {
          const { body: { data: invitingClients } } = response
          if (invitingClients?.length && invitingClients.some(client => !client.isInviter)) {
            WebStorageService.setItem(WebStorageKey.OPEN_DASHBOARD_CLIENT_INVITATIONS, true)
            openClientInvitationsModal()
          }
        })
        .catch(onFetchError)
    }

    CelloService.boot()
    return subscriptionDetail
  }

  const userInfoPromise = withUserInfo ? store.dispatch(fetchData()) : Promise.resolve()

  return Promise.all([fetchSubscriptionData(force), userInfoPromise])
    .then(([subscriptionData]) => setSubscriptionState(subscriptionData))
    .catch(onFetchError)
}

export const isAndroidClient = () => {
  return orgamax.user.get('registrationClient') && orgamax.user.get('registrationClient').toLowerCase() === 'android'
}

export const fetchActiveDetails = () => {
  return new Promise((resolve, reject) => {
    request(settingsConfig.getSubscriptionDetails, { auth: true, isConcurrent: true })
      .then(({ body: { data: { activeDetails } } }) => resolve(activeDetails))
      .catch(reject)
  })
}
